import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  computeTotals,
  PACKAGE_LAYOUT_DEFS,
  PACKAGE_PRICING_MAP,
  type PackageLayout,
  type PackagePricing,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { money, plural, WORDS } from '../../lib/format';
import { Badge, EmptyState, Modal } from '../../components/ui';
import type { EditableLine } from './LineEditor';

interface PackageOption {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  pricingMode: PackagePricing;
  layout: PackageLayout;
  durationDays: number | null;
  itemCount: number;
  pricing: { listTotal: number; price: number; saving: number; savingPercent: number };
}

interface ExpandedLine {
  serviceId: string | null;
  description: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  discount: number;
}

/**
 * إدراج باقة في مستند.
 *
 * التوسيع يتم على الخادم لا في الواجهة: توزيع السعر المقطوع على البنود
 * حساب دقيق بالقرش، وتكراره هنا يعني نسختين قد تختلفان. ما يُعاين هو ما
 * سيُحفظ حرفيًا.
 */
export function PackagePicker({
  onInsert,
  onClose,
}: {
  onInsert: (lines: EditableLine[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<PackageOption | null>(null);
  const [layout, setLayout] = useState<PackageLayout | ''>('');
  const [quantity, setQuantity] = useState('1');

  const list = useQuery({
    queryKey: ['packages', 'picker'],
    queryFn: () => api<{ items: PackageOption[] }>('/sales/packages?pageSize=100&isActive=true'),
  });

  const effectiveLayout = layout || selected?.layout || 'DETAILED';

  const preview = useQuery({
    queryKey: ['packages', 'expand', selected?.id, effectiveLayout, quantity],
    enabled: Boolean(selected),
    queryFn: () =>
      api<{ lines: ExpandedLine[] }>(`/sales/packages/${selected!.id}/expand`, {
        method: 'POST',
        body: { layout: effectiveLayout, quantity: Number(quantity) || 1 },
      }),
  });

  const insert = useMutation({
    mutationFn: async () => {
      const lines = preview.data?.lines ?? [];
      return lines.map<EditableLine>((l) => ({
        serviceId: l.serviceId,
        description: l.description,
        quantity: String(l.quantity),
        unit: l.unit,
        unitPrice: String(l.unitPrice),
        discount: l.discount ? String(l.discount) : '',
      }));
    },
    onSuccess: (lines) => {
      onInsert(lines);
      onClose();
    },
  });

  const previewTotal = preview.data
    ? computeTotals(
        preview.data.lines.map((l) => ({
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          discount: l.discount,
        })),
      ).subtotal
    : 0;

  return (
    <Modal
      title="إدراج باقة"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => insert.mutate()}
            disabled={!preview.data || preview.isFetching}
          >
            إدراج البنود
          </button>
        </>
      }
    >
      {list.data?.items.length === 0 && (
        <EmptyState
          title="لا توجد باقات"
          text="أنشئ باقة من كتالوج الخدمات لتظهر هنا جاهزة للإدراج."
        />
      )}

      {list.data && list.data.items.length > 0 && (
        <>
          <h4 className="section-title">اختر الباقة</h4>

          <div className="col" style={{ gap: 8 }}>
            {list.data.items.map((p) => (
              <label
                key={p.id}
                className={`pick-card ${selected?.id === p.id ? 'is-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="package"
                  checked={selected?.id === p.id}
                  onChange={() => {
                    setSelected(p);
                    setLayout('');
                  }}
                />
                <div className="col" style={{ gap: 2, flex: 1 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="strong">{p.name}</span>
                    <Badge kind={PACKAGE_PRICING_MAP[p.pricingMode].kind}>
                      {PACKAGE_PRICING_MAP[p.pricingMode].label}
                    </Badge>
                  </div>
                  <span className="tiny mute">
                    {plural(p.itemCount, WORDS.service)}
                    {p.durationDays ? ` — ${plural(p.durationDays, WORDS.day)} للتنفيذ` : ''}
                  </span>
                </div>
                <div className="col end" style={{ gap: 2, textAlign: 'end' }}>
                  <span className="num strong">{money(p.pricing.price)}</span>
                  {p.pricing.saving > 0 && (
                    <span className="tiny pos num">
                      توفير {money(p.pricing.saving)} ({p.pricing.savingPercent}٪)
                    </span>
                  )}
                </div>
              </label>
            ))}
          </div>
        </>
      )}

      {selected && (
        <>
          <h4 className="section-title">شكل الإدراج</h4>

          <div className="form-grid">
            <div className="field">
              <label className="label" htmlFor="pick-layout">
                طريقة العرض
              </label>
              <select
                id="pick-layout"
                className="select"
                value={effectiveLayout}
                onChange={(e) => setLayout(e.target.value as PackageLayout)}
              >
                {PACKAGE_LAYOUT_DEFS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
              <span className="tiny mute">
                {PACKAGE_LAYOUT_DEFS.find((d) => d.key === effectiveLayout)?.hint}
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="pick-qty">
                عدد الباقات
              </label>
              <input
                id="pick-qty"
                className="input num"
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              <span className="tiny mute">
                مفيد للاشتراكات: ثلاثة أشهر من باقة شهرية مثلًا.
              </span>
            </div>
          </div>

          <h4 className="section-title">معاينة البنود</h4>

          {preview.isPending && <div className="skeleton" style={{ height: 90 }} />}

          {preview.data && (
            <>
              <div className="table-wrap">
                <table className="table lines-table">
                  <thead>
                    <tr>
                      <th>البند</th>
                      <th style={{ width: 80 }}>الكمية</th>
                      <th style={{ width: 120 }}>سعر الوحدة</th>
                      <th style={{ width: 110 }}>خصم الباقة</th>
                      <th style={{ width: 120 }}>الإجمالي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.lines.map((l, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'pre-wrap' }}>{l.description}</td>
                        <td className="num">{l.quantity}</td>
                        <td className="num">{money(l.unitPrice)}</td>
                        <td className="num">{l.discount ? money(l.discount) : '—'}</td>
                        <td className="num strong">
                          {money(l.quantity * l.unitPrice - l.discount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="totals-box mt-4">
                <div className="totals-row totals-final">
                  <span>ما سيُضاف إلى المستند</span>
                  <span className="num">{money(previewTotal)}</span>
                </div>
              </div>
            </>
          )}

          {preview.isError && (
            <div className="alert alert-danger mt-2">تعذّر تجهيز بنود الباقة.</div>
          )}
        </>
      )}
    </Modal>
  );
}
